import { isDesktop, isIOS, isNativeApp } from '../../../client'
import { IPage } from '../../../types'
import { IStory, IStoryCard } from '../../../types/stories'
import { sendGA4, t_event_name } from '../../../utils'
import { StoryStatus } from './storiesState'

/**
 * GA4 events of the Stories block ("Analytics Spec - Stories Component",
 * D39). Every parameter of an event's groups is always sent, "" when it has
 * no value (GA shows "(not set)"); sendGA4 adds the page's own parameters.
 */

/** a card's progress events, below 100 (100 is its end) */
export const STORIES_MILESTONES = [25, 50, 75, 90]

export type StoriesEntry = 'stories_rail' | 'deeplink'

type Params = Record<string, string | number>

export interface IStoriesContext {
  gridId: number
  /** the block's place on the page, 1 = top; "" when the page does not say */
  position: number | ''
  hasAdblock: 'yes' | 'no' | ''
  entry: StoriesEntry | ''
}

export type StoriesPlatform =
  | 'desktop'
  | 'mobile_web'
  | 'app_ios'
  | 'app_android'

export const storiesPlatform = (): StoriesPlatform => {
  if (isNativeApp()) {
    return isIOS() ? 'app_ios' : 'app_android'
  }
  return isDesktop() ? 'desktop' : 'mobile_web'
}

const BUBBLE_STATE: Record<StoryStatus, string> = {
  new: 'unwatched',
  unwatched: 'unwatched',
  progress: 'in_progress',
  watched: 'watched',
}

/** Base: on every event; the story and the card when the event has them */
export const baseParams = (
  context: IStoriesContext,
  story?: IStory,
  card?: IStoryCard
): Params => ({
  platform: storiesPlatform(),
  grid_id: context.gridId,
  component_position: context.position,
  story_title: story?.storyTitle ?? '',
  // the CMS sends the full episode's ReferenceId as the series name
  full_episode_reference_id: story?.seriesName ?? '',
  has_adblock: context.hasAdblock,
  entry_source: context.entry,
  card_index: card?.order ?? '',
  // empty, not the page video's that sendGA4 adds; card events send their card's
  house_number: '',
})

/** "פרק 1:" as the rail shows it, without the colon */
export const episodeLabel = (story: IStory) =>
  story.boldTitle.replace(/:\s*$/, '')

/** `position`: the bubble's place in the rail, 1 = right */
export const storyParams = (
  story: IStory,
  position: number,
  status: StoryStatus
): Params => ({
  story_id: story.id,
  story_episode_number: episodeLabel(story),
  episode_number: story.episodeNumber,
  series_id: story.seriesId,
  series_name: story.seriesName,
  bubble_position: position,
  bubble_state: BUBBLE_STATE[status],
  total_cards: story.cards.length,
})

export const cardParams = (card: IStoryCard, autoplay: boolean): Params => ({
  ReferenceId: card.name,
  kaltura_id: card.kalturaId,
  // 0 is a duration the CMS did not send
  video_duration: card.duration > 0 ? card.duration : '',
  is_autoplay: autoplay ? 'yes' : 'no',
  house_number: card.onAirID,
})

/** the milestones `time` has passed that were not sent yet, in order */
export const milestonesReached = (
  time: number,
  duration: number,
  sent: ReadonlySet<number>
) =>
  duration > 0
    ? STORIES_MILESTONES.filter(
        m => !sent.has(m) && time >= (duration * m) / 100
      )
    : []

export const sendStoriesEvent = (
  pageData: IPage | null,
  event: string,
  params: Params
) =>
  sendGA4({
    eventName: `stories_${event}` as t_event_name,
    pageData,
    ...params,
  })
